import { useQuery } from "@apollo/client";
import { loader } from "graphql.macro";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import {
  hasFeature,
  voiceTranslationEnabledForOrganization,
} from "../../lib/candyvoice";
import {
  MAX_VOCABULARIES_PER_MEETING,
  VocabularySummary,
} from "../../lib/vocabularies";

const GET_ORGANIZATION_VOCABULARIES_QUERY = loader(
  "../../schema/query/GetOrganizationVocabularies.graphql"
);

interface Data {
  organization: {
    id: string;
    options?: Record<string, any> | null;
    features: string[];
    voiceTranslation: boolean;
    vocabularies: VocabularySummary[];
  };
}

interface Props {
  organizationId: string | null;
  // the vocabularies chosen for the meeting, undefined for the default ones of
  // the organization
  value: string[] | undefined;
  onChange: (names: string[]) => void;
}

// Choose the CandyVoice vocabularies of a meeting, in the options of the
// meeting forms, for the organizations having the voice translation. See
// src/lib/vocabularies.ts
export const VocabulariesOptions: React.FC<Props> = ({
  organizationId,
  value,
  onChange,
}) => {
  const { data } = useQuery<Data>(GET_ORGANIZATION_VOCABULARIES_QUERY, {
    variables: { id: organizationId },
    skip: !organizationId,
  });
  // the vocabularies of its own are Pro Security and above, see
  // Meetingrooms.Plans in the backend
  const vocabularies = (data?.organization.vocabularies ?? []).filter(
    (vocabulary) =>
      vocabulary.common || hasFeature(data?.organization, "custom_vocabulary")
  );
  if (!voiceTranslationEnabledForOrganization(data?.organization)) return null;
  if (vocabularies.length === 0) return null;

  const selected =
    value ?? vocabularies.filter((v) => v.default).map((v) => v.name);

  return (
    <>
      <Typography sx={{ color: "text.secondary", pt: 1 }}>
        <FormattedMessage
          id="vocabularies.meeting"
          defaultMessage="Vocabularies ({max} at most)"
          values={{ max: MAX_VOCABULARIES_PER_MEETING }}
        />
      </Typography>
      {vocabularies.map((vocabulary) => {
        const checked = selected.includes(vocabulary.name);
        return (
          <FormControlLabel
            key={vocabulary.name}
            control={
              <Checkbox
                checked={checked}
                disabled={
                  !checked && selected.length >= MAX_VOCABULARIES_PER_MEETING
                }
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, vocabulary.name]
                      : selected.filter((name) => name !== vocabulary.name)
                  )
                }
              />
            }
            label={vocabulary.name}
          />
        );
      })}
    </>
  );
};
